import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import React, { forwardRef, useMemo } from 'react';
import classnames from 'clsx';

import style from './pill.module.less';
import { ColorService } from '../../services/color-service';
import { GuidService } from '../../services/guid-service';
import { testId } from '../../services/testid-service';
import { Button } from '../button';
import { Tooltip } from '../tooltip';

/**
 * @typedef {import('./pill-types.d.ts').PillComponent} PillComponent
 */

/**
 * @typedef {import('./pill-types.d.ts').RenderPillRemoverFunction} RenderPillRemoverFunction
 */

/**
 * @type {RenderPillRemoverFunction}
 */
const renderPillRemover = (onRemove, label, id) => {
  if (!onRemove) {
    return null;
  }
  return (
    <Button className={style['icon-wrapper']} onClick={() => onRemove(label, id)} testId='pill-remove-button' theme='unstyled'>
      <FontAwesomeIcon className={style.icon} icon={faXmark} />
    </Button>
  );
};

/**
 * @type {PillComponent}
 */
export const Pill = forwardRef(
  (
    { id, icon, onRemove, label, theme = 'no-status', className = '', color = '', truncate = false, collapsed = false, textClassName },
    ref,
  ) => {
    const safeId = useMemo(() => {
      if (id) {
        return id;
      }
      return GuidService.newGuid();
    }, [id]);
    const calculatedTheme = theme === 'no-status' && color ? 'tag' : theme;
    const pillClassNames = classnames(style.pill, style[calculatedTheme], className, {
      [style.removable]: !!onRemove,
      [style['white-text']]: calculatedTheme === 'tag' && color && ColorService.shouldUseWhiteText(color),
      [style.truncate]: truncate,
      [style['white-tag']]: calculatedTheme === 'tag' && color && (color.toUpperCase() === '#FFFFFF' || color.toUpperCase() === '#FFF'),
      [style.collapsed]: collapsed,
    });
    const pillStyle = {};
    if (calculatedTheme === 'tag') {
      pillStyle.background = color;
    }
    if (collapsed) {
      return (
        <Tooltip position='bottom' text={label}>
          <div ref={ref} className={pillClassNames} style={pillStyle} {...testId('pill')} />
        </Tooltip>
      );
    }
    return (
      <div ref={ref} className={pillClassNames} style={pillStyle} {...testId('pill')}>
        {icon}
        <div className={style['pill-content']}>
          <span className={textClassName}>{label}</span>
          {label ? renderPillRemover(onRemove, label, safeId) : null}
        </div>
      </div>
    );
  },
);
